/**
 * ProcessingScreen — shown when workflow_state is `service_selected`.
 *
 * The admin has not yet approved the AI agent response. The customer
 * simply sees a reassuring wait screen with a manual refresh option.
 */
import { Hourglass, RefreshCw, Lock, Sparkles, UserCheck, CheckCircle2 } from 'lucide-react';

const FOREST = 'hsl(var(--palette-forest, 176 72% 17%))';
const AMBER  = 'hsl(var(--palette-amber,   33 100% 50%))';

interface Props {
  onRefresh: () => void;
  isRefreshing?: boolean;
}

const TRUST_BADGES = [
  { Icon: Lock,      label: 'ERI Secured'    },
  { Icon: Sparkles,  label: 'AI Assisted'    },
  { Icon: UserCheck, label: 'Expert Reviewed' },
];

export function ProcessingScreen({ onRefresh, isRefreshing = false }: Props) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-12 sm:py-16 text-center animate-fade-in">
      {/* Animated icon — outer ping ring + slow pulse for the hourglass. */}
      <div className="relative mb-8">
        <span
          aria-hidden
          className="absolute inset-0 rounded-full animate-ping"
          style={{ backgroundColor: `${FOREST}18` }}
        />
        <div
          className="relative h-24 w-24 rounded-full flex items-center justify-center border-2 animate-float-slow"
          style={{
            backgroundColor: `${FOREST}12`,
            borderColor:     `${FOREST}30`,
          }}
        >
          <Hourglass
            className="h-10 w-10 animate-pulse"
            style={{ color: FOREST }}
            aria-hidden
          />
        </div>
      </div>

      <h2
        className="font-display font-bold text-2xl sm:text-3xl leading-tight mb-3 max-w-sm animate-slide-up-fade stagger-1"
        style={{ color: 'hsl(var(--foreground))' }}
      >
        Thank you for your interest!
      </h2>

      <p className="text-sm sm:text-base text-muted-foreground max-w-md leading-relaxed mb-4 animate-slide-up-fade stagger-2">
        Our platform is securely analysing your information. This usually takes
        a few minutes. We'll update this page as soon as it's ready.
      </p>

      {/* Sub-line — friendlier, italic, sits closer to the body paragraph. */}
      <p
        className="text-sm italic max-w-md leading-relaxed mb-5 animate-slide-up-fade stagger-3"
        style={{ color: FOREST, opacity: 0.75 }}
      >
        Our system is analysing your financial profile for better planning.
      </p>

      {/* Reassurance — nothing more is needed from the customer; the team follows up. */}
      <div
        className="flex items-start gap-2.5 rounded-2xl px-4 py-3 mb-8 max-w-md text-left animate-slide-up-fade stagger-4"
        style={{ backgroundColor: `${FOREST}0D` }}
      >
        <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" style={{ color: FOREST }} aria-hidden />
        <p className="text-sm leading-relaxed" style={{ color: FOREST }}>
          <span className="font-semibold">You're all set — nothing more is needed from you.</span>{' '}
          Our team will reach out to you as soon as it's ready.
        </p>
      </div>

      {/* Refresh button */}
      <button
        type="button"
        onClick={onRefresh}
        disabled={isRefreshing}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-60 active:scale-95 hover:shadow-lg animate-slide-up-fade stagger-5"
        style={{ backgroundColor: isRefreshing ? 'hsl(var(--muted))' : AMBER }}
      >
        <RefreshCw
          className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
          aria-hidden
        />
        {isRefreshing ? 'Refreshing…' : 'Refresh Status'}
      </button>

      {/* Trust badges */}
      <div className="flex flex-wrap items-center justify-center gap-4 mt-10 pt-8 border-t border-border w-full max-w-sm">
        {TRUST_BADGES.map(({ Icon, label }) => (
          <span
            key={label}
            className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}
